// src/pages/lms/LMSLayout.jsx — FIXED
// Fix: wraps LMSAccessProvider inside so useLMSAccess works everywhere
// All nav items included, staff sees only their permitted pages
// canAccess returns boolean — fixed to match useLMSAccess.js exactly

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import { LMSAccessProvider, useLMSAccess } from '../../hooks/useLMSAccess';
import {
  LayoutDashboard, Users, BookOpen, Calendar, Megaphone,
  GraduationCap, CreditCard, Clock, Library, Bell, Award,
  BarChart3, Shield, FileText, Book, ChevronRight, LogOut,
  Settings, UserCheck, Star, BookMarked, CalendarDays,
} from 'lucide-react';

const ALL_NAV = [
  { key:'dashboard',     label:'Dashboard',        icon:LayoutDashboard, path:'/lms/dashboard'      },
  { key:'students',      label:'Students',          icon:Users,           path:'/lms/students'       },
  { key:'classes',       label:'Classes',           icon:BookOpen,        path:'/lms/classes'        },
  { key:'timetable',     label:'Timetable',         icon:Clock,           path:'/lms/timetable'      },
  { key:'teachers',      label:'Teachers',          icon:GraduationCap,   path:'/lms/teachers'       },
  { key:'assignments',   label:'Assignments',       icon:Award,           path:'/lms/assignments'    },
  { key:'attendance',    label:'Attendance',        icon:UserCheck,       path:'/lms/attendance'     },
  { key:'courses',       label:'Courses',           icon:Book,            path:'/lms/courses'        },
  { key:'exams',         label:'Exams',             icon:FileText,        path:'/lms/exams'          },
  { key:'calendar',      label:'Calendar',          icon:CalendarDays,    path:'/lms/calendar'       },
  { key:'library',       label:'Library',           icon:Library,         path:'/lms/library'        },
  { key:'fees',          label:'Fees',              icon:CreditCard,      path:'/lms/fees'           },
  { key:'feed',          label:'Feed',              icon:Megaphone,       path:'/lms/feed'           },
  { key:'events',        label:'Events',            icon:Calendar,        path:'/lms/events'         },
  { key:'notices',       label:'Notices',           icon:BookMarked,      path:'/lms/notices'        },
  { key:'notifications', label:'Notifications',     icon:Bell,            path:'/lms/notifications'  },
  { key:'reports',       label:'Reports',           icon:BarChart3,       path:'/lms/reports'        },
  { key:'settings',      label:'Access Control',    icon:Shield,          path:'/lms/access'         },
];

// Inner layout — uses useLMSAccess safely inside provider
function LMSLayoutInner({ children }) {
  const { account, institution, signOut } = useLMSAuth();
  const { staffSession, canAccess, canEdit, staffLogout } = useLMSAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = !!account;

  // Filter nav for staff
  const navItems = ALL_NAV.filter(item => {
    if (isAdmin) return true;
    if (!staffSession) return false;
    return canAccess(item.key); // returns boolean
  });

  const displayName = isAdmin
    ? (account?.lms_username || account?.email?.split('@')[0] || 'Admin')
    : (staffSession?.staff_name || 'Staff');

  const displayRole = isAdmin
    ? '⭐ Admin'
    : `${staffSession?.role_emoji || '👤'} ${staffSession?.role_name || 'Staff'}`;

  const handleLogout = async () => {
    if (isAdmin) {
      await signOut?.();
      navigate('/lms');
    } else {
      staffLogout();
      navigate('/lms/staff-login');
    }
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F1F5F9' }}>

      {/* ── SIDEBAR ── */}
      <div style={{
        width:240, height:'100vh', background:'#fff',
        borderRight:'1px solid #E5E7EB', display:'flex',
        flexDirection:'column', position:'sticky', top:0,
        flexShrink:0, overflowY:'auto',
        zIndex: 1000,
      }}>
        {/* Logo */}
        <div style={{ padding:'20px 16px 14px', borderBottom:'1px solid #F1F5F9' }}>
          <Link to="/lms/dashboard" style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{
              width:40, height:40, borderRadius:12, flexShrink:0,
              background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              <span style={{ fontSize:20 }}>🏫</span>
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:900, color:'#111827', lineHeight:1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {institution?.name || 'UNIVERSE'}
              </div>
              <div style={{ fontSize:10, color:'#9CA3AF', marginTop:1 }}>LMS Portal</div>
            </div>
          </Link>
        </div>

        {/* User badge */}
        <div style={{ padding:'10px 14px', borderBottom:'1px solid #F1F5F9', background:'#FAFBFF' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{
              width:34, height:34, borderRadius:10, flexShrink:0,
              background: isAdmin
                ? 'linear-gradient(135deg,#F59E0B,#EF4444)'
                : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'#fff', fontWeight:800, fontSize:15,
            }}>
              {(displayName[0] || 'A').toUpperCase()}
            </div>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {displayName}
              </div>
              <div style={{ fontSize:10, fontWeight:700, color: isAdmin ? '#F59E0B' : '#7C3AED' }}>
                {displayRole}
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'8px 8px', overflowY:'auto' }}>
          {navItems.map(item => {
            const Icon    = item.icon;
            const active  = location.pathname === item.path || location.pathname.startsWith(item.path+'/');
            const viewOnly= !isAdmin && staffSession && !canEdit(item.key);
            return (
              <Link key={item.key} to={item.path} style={{ textDecoration:'none', display:'block', marginBottom:2 }}>
                <div style={{
                  display:'flex', alignItems:'center', gap:10,
                  padding:'9px 12px', borderRadius:10,
                  background: active ? 'linear-gradient(135deg,#EFF6FF,#F5F3FF)' : 'transparent',
                  border: active ? '1px solid #BFDBFE' : '1px solid transparent',
                  cursor:'pointer',
                }}
                  onMouseEnter={e=>{ if(!active) e.currentTarget.style.background='#F8FAFF'; }}
                  onMouseLeave={e=>{ if(!active) e.currentTarget.style.background='transparent'; }}
                >
                  <Icon size={16} color={active ? '#3B6EE8' : '#6B7280'}/>
                  <span style={{
                    flex:1, fontSize:13,
                    fontWeight: active ? 800 : 500,
                    color: active ? '#1E293B' : '#374151',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                  }}>
                    {item.label}
                  </span>
                  {viewOnly && !active && (
                    <span style={{ fontSize:8, color:'#F59E0B', background:'#FEF3C7', padding:'1px 4px', borderRadius:3, fontWeight:800, flexShrink:0 }}>
                      VIEW
                    </span>
                  )}
                  {active && <ChevronRight size={13} color="#3B6EE8"/>}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div style={{ padding:'8px 8px 12px', borderTop:'1px solid #F1F5F9' }}>
          {isAdmin && (
            <Link to="/lms/change-password" style={{ textDecoration:'none', display:'block', marginBottom:2 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:10, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='#F8FAFF'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Settings size={16} color="#6B7280"/>
                <span style={{ fontSize:13, color:'#374151', fontWeight:500 }}>Change Password</span>
              </div>
            </Link>
          )}
          <button onClick={handleLogout} style={{
            width:'100%', display:'flex', alignItems:'center', gap:10,
            padding:'9px 12px', borderRadius:10, border:'none',
            background:'transparent', cursor:'pointer', textAlign:'left',
          }}
            onMouseEnter={e=>e.currentTarget.style.background='#FEF2F2'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}
          >
            <LogOut size={16} color="#EF4444"/>
            <span style={{ fontSize:13, color:'#EF4444', fontWeight:600 }}>
              {isAdmin ? 'Sign Out' : 'Staff Logout'}
            </span>
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
        {/* Top bar */}
        <div style={{
          background:'#fff', borderBottom:'1px solid #E5E7EB',
          padding:'12px 24px', display:'flex', alignItems:'center',
          justifyContent:'space-between', position:'sticky', top:0, zIndex:100,
          boxShadow:'0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:'#111827' }}>
              {ALL_NAV.find(n => location.pathname === n.path || location.pathname.startsWith(n.path+'/'))?.label || 'LMS'}
            </div>
            <div style={{ fontSize:11, color:'#9CA3AF', marginTop:1 }}>{institution?.name}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{
              padding:'5px 12px', borderRadius:8,
              background: isAdmin ? '#FEF3C7' : '#F5F3FF',
              border:`1px solid ${isAdmin ? '#FED7AA' : '#DDD6FE'}`,
              fontSize:11, fontWeight:800,
              color: isAdmin ? '#92400E' : '#7C3AED',
            }}>
              {displayRole}
            </div>
            <div style={{
              width:34, height:34, borderRadius:10,
              background: isAdmin
                ? 'linear-gradient(135deg,#F59E0B,#EF4444)'
                : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'#fff', fontWeight:800, fontSize:15,
            }}>
              {(displayName[0]||'A').toUpperCase()}
            </div>
          </div>
        </div>

        {/* Page */}
        <div style={{ flex:1, padding:'24px 24px 40px', overflowY:'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// Outer wrapper — provides LMSAccessProvider so useLMSAccess works inside
export default function LMSLayout({ children }) {
  const { account, institution } = useLMSAuth();
  const isAdmin = !!account;

  // Get institution ID from admin session OR from staff session in localStorage
  const getInstId = () => {
    if (institution?.id) return institution.id;
    try {
      const s = localStorage.getItem('lms_staff_session');
      if (s) return JSON.parse(s)?.institution_id || null;
    } catch(_) {}
    try { return localStorage.getItem('lms_institution_id') || null; } catch(_) {}
    return null;
  };

  return (
    <LMSAccessProvider institutionId={getInstId()} isAdmin={isAdmin}>
      <LMSLayoutInner>
        {children}
      </LMSLayoutInner>
    </LMSAccessProvider>
  );
}